import {
  getStorePageLinks,
  publishEvent,
  StorePageLinksProps
} from '@uma/react-commons/dist/index';
import { IconMonitorSearch, IconShoppingCartCheckmark } from '@veneer/core';
import React, { useEffect, useState } from 'react';
import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import { FeatureFlagProps } from 'src/pages/Orders';
import { URLS } from 'src/utils/urls';
import Loader from '../Loader';
import {
  GuestOrdersHyperLinkClicked,
  ViewOrderHistoryHyperLinkClicked
} from './analytics';
import { Card, ClickableLabel, Container, Link } from './styles';

export default function ManageOrdersSection({
  isLoading,
  ordersCountryBasedExperience
}: FeatureFlagProps): React.JSX.Element {
  const { translate, northboundAPIs, logger } = useDependencyManagerContext();
  const { language } = northboundAPIs.v1.localization;

  const [isFetching, setIsFetching] = useState(true);
  const [urlOrderHistory, setUrlOrderHistory] = useState<string | null>(null);
  const [urlGuestOrder, setUrlGuestOrder] = useState<string | null>(null);

  useEffect(() => {
    if (isLoading) return;

    if (!isLoading && !ordersCountryBasedExperience) {
      setIsFetching(false);
      return;
    }

    async function fetchData() {
      try {
        await Promise.all([fetchOrderHistory(), fetchGuestOrder()]);
      } catch (error) {
        logger?.error('Error fetching data:', error);
      } finally {
        setIsFetching(false);
      }
    }

    async function fetchOrderHistory() {
      try {
        const response = await getStorePageLinks(
          language,
          StorePageLinksProps.OrderHistory
        );
        setUrlOrderHistory(response);
      } catch (error) {
        setUrlOrderHistory(URLS.OrderHistory);
        logger?.error('Error fetching order history:', error);
      }
    }

    async function fetchGuestOrder() {
      try {
        const response = await getStorePageLinks(
          language,
          StorePageLinksProps.GuestOrderLookup
        );
        setUrlGuestOrder(response);
      } catch (error) {
        setUrlGuestOrder(URLS.GuestOrders);
        logger?.error('Error fetching guest order:', error);
      }
    }

    fetchData();
  }, [isLoading, language, logger, ordersCountryBasedExperience]);

  const showCardOrderHistory = ordersCountryBasedExperience
    ? !!urlOrderHistory
    : true;
  const showCardGuestOrder = ordersCountryBasedExperience
    ? !!urlGuestOrder
    : true;

  const contents = [];

  if (showCardOrderHistory) {
    contents.push({
      title: {
        content: translate('orders.orderHistory', 'Order history'),
        postIcon: <IconShoppingCartCheckmark />
      },
      rows: [
        {
          content: translate(
            'orders.cardOrderHistory',
            'View and manage your purchases, along with accessing order tracking to your orders that were made while signed in.'
          ) as unknown as string,
          index: 0
        },
        {
          content: (
            <Link>
              {
                translate(
                  'orders.viewOrderHistory',
                  'View order history'
                ) as unknown as string
              }
            </Link>
          ),
          data: {
            href: urlOrderHistory || URLS.OrderHistory,
            onClick: () => publishEvent(ViewOrderHistoryHyperLinkClicked)
          },
          index: 1
        }
      ]
    });
  }

  if (showCardGuestOrder) {
    contents.push({
      title: {
        content: translate('orders.guestOrder', 'Guest orders'),
        postIcon: <IconMonitorSearch />
      },
      rows: [
        {
          content: translate(
            'orders.findYourStatus',
            'Find your order status on purchases that were made while not signed in.'
          ) as unknown as string,
          index: 0
        },
        {
          content: (
            <Link>
              {
                translate(
                  'orders.findGuestOrder',
                  'Find guest order'
                ) as unknown as string
              }
            </Link>
          ),
          data: {
            href: urlGuestOrder || URLS.GuestOrders,
            onClick: () => publishEvent(GuestOrdersHyperLinkClicked)
          },
          index: 1
        }
      ]
    });
  }

  return (
    <Container
      data-testid="container-card"
      aria-label="Manage Orders Section"
    >
      {contents.map((content, index) => {
        if (isLoading || isFetching) {
          return (
            <Loader
              key={content.rows[index].index}
              data-testid="loading-container-card"
            />
          );
        }

        return (
          <ClickableLabel
            key={content.rows[index].index}
            href={content.rows[1]?.data?.href}
            target="_blank"
            rel="noreferrer"
            onClick={content.rows[1]?.data?.onClick}
            tabIndex={0}
          >
            <Card
              rows={content.rows}
              showDivider={false}
              rowsColorChangeEnabled={false}
              title={content.title}
            />
          </ClickableLabel>
        );
      })}
    </Container>
  );
}
